// Promo code labels: letters and digits only, stored uppercase.
export const DISCOUNT_CODE_NAME_PATTERN = /^[A-Z0-9]+$/;

export const normalizeDiscountCodeName = (value: string) => value.trim().toUpperCase();

import { Discount, DiscountType } from "@prisma/client";
import { convertToDecimalFraction } from "./prices";

type MinimalDiscount = Omit<Discount, "id" | "createdAt" | "updatedAt">;

export const computeDiscount = (
  price: number,
  discount: MinimalDiscount
): number => {
  if (discount.type === DiscountType.euro) {
    return Math.min(price, discount.value);
  }
  if (discount.type === DiscountType.percentage) {
    return Math.round(price * (convertToDecimalFraction(discount.value) / 100));
  }
  return 0;
};

export const applyDiscount = (
  price: number,
  discount: MinimalDiscount
): number => {
  return price - computeDiscount(price, discount);
};

type AppliedCodeItem = {
  id: string;
  codeName: string;
  appliedAmount: number;
};

type ItemBreakdownWithAppliedCode = {
  appliedCode?: AppliedCodeItem[];
};

export type AggregatedDiscount = {
  codeId: string;
  name: string;
  appliedAmount: number;
};

export const aggregateDiscountsByCode = (
  itemBreakdowns: ItemBreakdownWithAppliedCode[]
): AggregatedDiscount[] => {
  const codeAmounts = new Map<string, { name: string; amount: number }>();

  itemBreakdowns.forEach((breakdown) => {
    breakdown.appliedCode?.forEach((code) => {
      const existing = codeAmounts.get(code.id);
      if (existing) {
        existing.amount += code.appliedAmount;
      } else {
        codeAmounts.set(code.id, {
          name: code.codeName,
          amount: code.appliedAmount,
        });
      }
    });
  });

  return Array.from(codeAmounts.entries()).map(([codeId, { name, amount }]) => ({
    codeId,
    name,
    appliedAmount: amount,
  }));
};

type AppliedDiscountCodeUnits = { appliedUnits: number; skippedUnits: number };

// A code whose budget ran out before every eligible unit of the basket was discounted.
export const isDiscountCodePartiallyApplied = ({ skippedUnits }: AppliedDiscountCodeUnits): boolean =>
  skippedUnits > 0;

export const getPartialDiscountCodeMessage = ({ appliedUnits }: AppliedDiscountCodeUnits): string =>
  appliedUnits === 0
    ? "Ce code a atteint sa limite d'utilisation : aucun article du panier n'est remisé."
    : `Ce code s'applique à ${appliedUnits} article${appliedUnits > 1 ? "s" : ""} au maximum ; les autres articles restent au tarif normal.`;

// "used / total" for the backoffice tables; one use is one discounted unit. Unlimited totals show ∞.
export const formatDiscountCodeUses = ({
  usesCount,
  quantity,
  isQuantityInfinite,
}: {
  usesCount: number;
  quantity: number | null;
  isQuantityInfinite: boolean;
}): string => `${usesCount} / ${isQuantityInfinite || quantity === null ? "∞" : quantity}`;
